import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Check, Sparkles, Loader2 } from "lucide-react";
import { OnboardingData } from "../index";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface SelectPlanProps {
  onNext: () => void;
  onPrevious: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

interface PlanFeature {
  title: string;
  included: boolean;
}

interface Plan {
  id: string;
  name: string;
  price: string;
  description: string;
  features: PlanFeature[];
  badge?: string;
}

export default function SelectPlan({
  onNext,
  onPrevious,
  onboardingData,
  updateOnboardingData,
}: SelectPlanProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>(
    onboardingData.subscriptionPlanId || "zentroseed"
  );
  
  const plans: Plan[] = [
    {
      id: "zentroseed",
      name: "ZentroSeed",
      price: "$39",
      description: "Perfect for new businesses just starting out",
      badge: "MOST POPULAR",
      features: [
        { title: "Basic financial reports", included: true },
        { title: "Quarterly balance sheet generation", included: true },
        { title: "3-month transaction history", included: true },
        { title: "CSV data export", included: true },
        { title: "Single business management", included: true },
        { title: "AI-powered transaction categorization", included: true },
        { title: "Basic expense tracking", included: true },
        { title: "Monthly financial insights", included: false },
        { title: "Advanced reporting tools", included: false },
        { title: "AI tax assistant", included: false },
      ],
    },
    {
      id: "zentrosprout",
      name: "ZentroSprout",
      price: "$59",
      description: "For growing businesses with more complex needs",
      features: [
        { title: "Enhanced financial reports with trends", included: true },
        { title: "Monthly balance sheet generation", included: true },
        { title: "12-month transaction history", included: true },
        { title: "CSV & Excel data export", included: true },
        { title: "Multi-business management", included: true },
        { title: "AI-powered transaction categorization", included: true },
        { title: "Advanced expense tracking & bills", included: true },
        { title: "Monthly financial insights", included: true },
        { title: "Basic forecasting tools", included: true },
        { title: "AI tax assistant with 20 queries/month", included: true },
      ],
    },
    {
      id: "zentroscale",
      name: "ZentroScale",
      price: "$99",
      description: "For established businesses requiring comprehensive tools",
      features: [
        { title: "Advanced reports with year-over-year comparisons", included: true },
        { title: "Daily balance sheet updates", included: true },
        { title: "Unlimited transaction history", included: true },
        { title: "Full data export in multiple formats", included: true },
        { title: "Unlimited business management", included: true },
        { title: "Advanced AI transaction processing", included: true },
        { title: "Complete expense & contractor management", included: true },
        { title: "Advanced financial insights & alerts", included: true },
        { title: "Comprehensive forecasting tools", included: true },
        { title: "Unlimited AI tax assistant usage", included: true },
      ],
    },
  ];
  
  const handleSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
  };
  
  const handleContinue = async () => {
    try {
      setIsLoading(true);
      
      // Get the plan tier number based on selection
      let tierLevel = 1; // Default to ZentroSeed
      if (selectedPlanId === "zentrosprout") tierLevel = 2;
      if (selectedPlanId === "zentroscale") tierLevel = 3;
      
      // Calculate trial end date (14 days from now)
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 14);
      
      // Update onboarding data
      updateOnboardingData({
        subscriptionPlanId: selectedPlanId,
        selectedPlan: tierLevel,
        trialEndDate: trialEndDate,
        billingCycle: "monthly",
      });
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Plan selected",
        description: `You've selected the ${plans.find(p => p.id === selectedPlanId)?.name} plan with a 14-day free trial.`,
      });
      
      // Proceed to next step
      onNext();
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem selecting your plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Choose Your Plan</h2>
          <p className="mt-2 text-neutral-600">
            Select the plan that best suits your business needs
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden transition-all ${
                selectedPlanId === plan.id 
                  ? "border-primary ring-2 ring-primary/20" 
                  : "hover:border-primary/50"
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 mt-4 mr-4">
                  <Badge variant="default" className="font-semibold bg-gradient-to-r from-violet-600 to-indigo-600">
                    {plan.badge}
                  </Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="flex items-center">
                  {plan.name}
                </CardTitle>
                <div className="flex items-baseline mt-2">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="ml-1 text-neutral-500">/month</span>
                </div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <div className={`mt-0.5 ${feature.included ? 'text-green-500' : 'text-neutral-300'}`}>
                        {feature.included ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <Check className="h-5 w-5" />
                        )}
                      </div>
                      <span className={`ml-2 text-sm ${feature.included ? 'text-neutral-700' : 'text-neutral-400 line-through'}`}>
                        {feature.title}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                <Button 
                  type="button"
                  variant={selectedPlanId === plan.id ? "default" : "outline"} 
                  className="w-full"
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  {selectedPlanId === plan.id ? "Selected" : "Select Plan"}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
        
        <div className="bg-primary/5 p-6 rounded-lg border border-primary/10">
          <div className="flex items-start space-x-3">
            <div className="bg-primary/20 p-2 rounded-full">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-lg mb-1">14-Day Free Trial</h3>
              <p className="text-neutral-600 mb-2">
                All plans come with a 14-day free trial. No credit card required until your trial ends.
              </p>
              <ul className="text-sm text-neutral-600 space-y-1 list-disc pl-5">
                <li>Free access to all features in your selected plan</li>
                <li>First billing starts after 14 days</li>
                <li>Cancel anytime during your trial</li>
                <li>Easy upgrade or downgrade between plans</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between pt-4">
          <Button type="button" variant="outline" onClick={onPrevious}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <Button 
            type="button" 
            onClick={handleContinue}
            disabled={isLoading || !selectedPlanId}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}